import { useEffect, useRef, useState } from 'react';
import { Maximize, MoreVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../services/authentication/authContext';
import { getImpersonationUserId, useImpersonationStore } from '../../../stores/appImpersonationStore';
import { canAccessRecipeViewer } from './recipeViewerAccess';

export function RecipeViewerMenu({ restaurantId }: { restaurantId?: string }) {
  const { t } = useTranslation();
  const { backendUser, accountSettings } = useAuth();
  const target = useImpersonationStore(s => s.target);
  const [open, setOpen] = useState(false);
  const [sessionChanged, setSessionChanged] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const item = useRef<HTMLAnchorElement>(null);
  const allowed = !sessionChanged && canAccessRecipeViewer(restaurantId, backendUser, accountSettings);

  useEffect(() => {
    const check = () => setSessionChanged(getImpersonationUserId() !== (target?.userId ?? null));
    window.addEventListener('storage', check);
    window.addEventListener('focus', check);
    return () => { window.removeEventListener('storage', check); window.removeEventListener('focus', check); };
  }, [target?.userId]);
  useEffect(() => { setOpen(false); }, [restaurantId, allowed]);
  useEffect(() => {
    if (!open) return;
    item.current?.focus();
    const outside = (event: MouseEvent) => { if (!container.current?.contains(event.target as Node)) setOpen(false); };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setOpen(false); trigger.current?.focus(); }
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('keydown', key); };
  }, [open]);
  if (!allowed) return null;
  return <div ref={container} className="relative flex-shrink-0" onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
  }}>
    <button ref={trigger} type="button" aria-haspopup="menu" aria-expanded={open}
      aria-label={t('recipeViewer.moreActions')} onClick={() => setOpen(value => !value)}
      onKeyDown={event => { if (event.key === 'ArrowDown') { event.preventDefault(); setOpen(true); } }}
      className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark bg-white dark:bg-background-dark text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark transition-colors">
      <MoreVertical className="w-5 h-5" />
    </button>
    {open && <div role="menu" className="absolute right-0 top-full mt-1 min-w-[200px] rounded-xl border border-[#E5E7EB] dark:border-borderPrimary-dark bg-white dark:bg-background-dark shadow-lg py-1 z-50">
      <a ref={item} role="menuitem" href={`/recipe-viewer/${restaurantId}/recipes`} target="_blank" rel="noopener noreferrer"
        onClick={() => { setOpen(false); trigger.current?.focus(); }}
        className="flex items-center gap-2 px-3 py-3 text-[14px] font-semibold text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark">
        <Maximize className="w-5 h-5" />{t('recipeViewer.fullScreenMode')}
      </a>
    </div>}
  </div>;
}
